import { Box, Typography } from '@mui/material'
import { ChevronRightIcon, InfoCircleIcon } from '@untitled-theme/icons-react'
import { useState } from 'react'

import { getRetrofitDisplayName } from '@/features/measures-overview/components/MeasuresTable/MeasuresTable.types'
import EditISFPDataPopup from '@/shared/components/EditISFPDataPopup'
import { RenovationCostsInfoPopup } from '@/shared/components/InfoPopup'
import { PropertyValueIncrease } from '@/shared/components/PropertyValueIncrease'
import { SavingsPotential } from '@/shared/components/SavingsPotential'
import type { RetrofitName, RetrofitState } from '@/shared/types'
import { getEnergyLabelColor } from '@/shared/utils/energyLabelColors'
import { getMeasureImage } from '@/shared/utils/measureImages'

import type { OverviewTabProps } from './OverviewTab.types'

// Helper function to get advantages text for each retrofit measure
const getRetrofitAdvantages = (retrofit: RetrofitName): string => {
  const advantages: Record<RetrofitName, string> = {
    exteriorWallETICS:
      'Eine optimierte Außenwanddämmung reduziert die Transmissionsverluste, verbessert die thermische Behaglichkeit und steigert die Energieeffizienz des Gebäudes.',
    airWaterHeatPump:
      'Eine Wärmepumpe nutzt Umweltenergie effizient und spart Heizkosten. Zudem reduziert sie den CO₂-Ausstoß und schont die Umwelt.',
    airAirHeatPump:
      'Eine Luft-Luft-Wärmepumpe gewinnt Wärme aus der Außenluft und verteilt sie über ein Luftsystem im Gebäude. Sie ermöglicht effizientes Heizen und Kühlen und senkt Energieverbrauch sowie CO₂-Ausstoß.',
    groundSourceHeatPump:
      'Eine Sole-Wärmepumpe nutzt die konstante Erdwärme über Erdsonden oder -kollektoren effizient. Hohe Jahresarbeitszahlen senken Heizkosten, reduzieren CO₂-Emissionen und steigern die Energieeffizienz.',
    pelletHeating:
      'Eine Pelletheizung nutzt Holzpellets als erneuerbaren Brennstoff und arbeitet mit hohen Wirkungsgraden. Dadurch sinken Heizkosten, fossiler Energieeinsatz und CO₂-Emissionen werden deutlich reduziert.',
    windows:
      'Der Austausch veralteter Fenster gegen moderne Wärmeschutzfenster reduziert den U-Wert erheblich, minimiert Wärmeverluste und spart nachhaltig Heizenergie.',
    roofWindows:
      'Neue Dachfenster mit hoher Wärmedämmqualität verbessern die thermische Hülle des Gebäudes, reduzieren Wärmebrücken und erhöhen die Energieeffizienz.',
    door: 'Der Einbau wärmegedämmter Türen verringert Energieverluste über den Eingangsbereich, optimiert die Gebäudehülle und verbessert den Luftdichtheitswert.',
    basementExteriorWall:
      'Durch die Dämmung von Kellerwänden werden Wärmebrücken reduziert und die Energieverluste ins Erdreich deutlich minimiert, was die Effizienz steigert.',
    basementCeiling:
      'Mit der Dämmung der Kellerdecke wird die thermische Trennung zwischen beheiztem Wohnraum und unbeheiztem Keller erreicht, was Heizenergie spart.',
    roof: 'Eine umfassende Dachdämmung senkt die Transmissionswärmeverluste durch die Dachfläche, verbessert den sommerlichen Wärmeschutz und reduziert die Heizlast.',
    topFloorCeiling:
      'Eine fachgerechte Dämmung der obersten Geschossdecke verhindert signifikante Wärmeverluste nach oben und senkt den Heizwärmebedarf.',
    floorPlate:
      'Die Dämmung der Bodenplatte verhindert Wärmeabfluss in den Untergrund und trägt zu einer ausgeglichenen und energieeffizienten Raumtemperatur bei.',
    decentralVentilation:
      'Lüftungsgeräte mit Wärmerückgewinnung ermöglichen den Luftaustausch in Räumen. Sie minimieren Wärmeverluste, beugen Feuchte- und Schimmelproblemen vor und senken den Energiebedarf.',
    blowInInsulation:
      'Die Einblasdämmung füllt Hohlräume in Wänden, Dächern oder Decken fugenlos und reduziert Wärmebrücken. Sie senkt Transmissionswärmeverluste und steigert Energieeffizienz schnell und wirtschaftlich.',
    districtHeating:
      'Ein Fernwärmeanschluss nutzt zentral erzeugte Wärme aus hocheffizienten und oft erneuerbaren Quellen. Er reduziert lokale Emissionen und senkt den Wartungsaufwand für die eigene Heiztechnik.',
  }
  return advantages[retrofit] || 'Diese Maßnahme trägt zur Verbesserung der Energieeffizienz bei.'
}

const getRetrofitExtraInfo = (retrofit: RetrofitState): JSX.Element | undefined => {
  const formatter = new Intl.NumberFormat('de-DE', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })

  const power = (type?: string) =>
    retrofit.details.powerInKw ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        {type ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '12px',
              }}
            >
              Art:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              {type}
            </Typography>
          </Box>
        ) : undefined}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '12px',
            }}
          >
            Leistung:
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            {formatter.format(retrofit.details.powerInKw)} kW
          </Typography>
        </Box>
      </Box>
    ) : undefined

  const showInsulationThickness =
    !!retrofit.details.insulationThicknessInMm && retrofit.retrofitName !== 'blowInInsulation'
  const uValuesBeforeMin = formatter.format(retrofit.details.uValues?.before.min ?? 0)
  const uValuesBeforeMax = formatter.format(retrofit.details.uValues?.before.max ?? 0)
  const uValues = retrofit.details.uValues ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '11px',
            color: '#464A54',
          }}
        >
          Aktueller U-Wert
        </Typography>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '12px',
          }}
        >
          {uValuesBeforeMin == uValuesBeforeMax
            ? `${uValuesBeforeMin} W/m²K`
            : `${uValuesBeforeMin} W/m²K – ${uValuesBeforeMax} W/m²K`}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
          gap: '20px',
          backgroundColor: '#F5F5F7',
          border: '1px solid #E1E1E4',
          borderRadius: '8px',
          padding: '14.5px 16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '12px',
              textAlign: 'center',
            }}
          >
            BEG Anforderungswert
          </Typography>
          <Typography>
            <Typography component="span" sx={{ fontWeight: 600, fontSize: '20px' }}>
              {formatter.format(
                (retrofit.details.uValues.after.min + retrofit.details.uValues.after.max) / 2
              )}
            </Typography>
            <Typography component="span" sx={{ fontWeight: 500, fontSize: '8px' }}>
              {' '}
              W/m²K
            </Typography>
          </Typography>
        </Box>
        {showInsulationThickness ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '12px',
              }}
            >
              Dämmstärke
            </Typography>
            <Typography>
              <Typography component="span" sx={{ fontWeight: 600, fontSize: '20px' }}>
                {formatter.format(retrofit.details.insulationThicknessInMm! / 10)} cm
              </Typography>
              <Typography component="span" sx={{ fontWeight: 500, fontSize: '8px' }}>
                {' '}
                (WLS35)
              </Typography>
            </Typography>
          </Box>
        ) : undefined}
      </Box>
    </Box>
  ) : undefined

  const advantages: Partial<Record<RetrofitName, JSX.Element>> = {
    // power measures
    airWaterHeatPump: power('Luft/Wasser'),
    airAirHeatPump: power('Luft/Luft'),
    groundSourceHeatPump: power('Sole/Wasser'),
    pelletHeating: power(),
    // u-value measures
    windows: uValues,
    roofWindows: uValues,
    door: uValues,
    basementExteriorWall: uValues,
    basementCeiling: uValues,
    roof: uValues,
    topFloorCeiling: uValues,
    exteriorWallETICS: uValues,
    floorPlate: uValues,
    // blowInInsulation: uValues, // Removed: blow-in insulation should not show BEG requirement value
  }
  return advantages[retrofit.retrofitName]
}

// Helper functions to format values
const formatAmortization = (years: number | null) => {
  if (!years) return 'k. A.'
  if (years > 40) return '+40 Jahre'
  return `${years} Jahre`
}

const formatCosts = (costsInCt: number) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(costsInCt / 100)
}

const formatSubsidy = (costsInCt: number | null, subsidyRate: number | null) => {
  if (!costsInCt || !subsidyRate) return 'k. A.'
  const subsidyAmount = (costsInCt / 100) * (subsidyRate / 100)
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(subsidyAmount)
}

const formatCostsAfterSubsidy = (costsInCt: number | null, subsidyRate: number | null) => {
  if (!costsInCt || !subsidyRate) return 'k. A.'
  const costsAfterSubsidy = (costsInCt / 100) * (1 - subsidyRate / 100)
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(costsAfterSubsidy)
}

const OverviewTab = ({ measureC, measureD, assessment }: OverviewTabProps) => {
  const displayName = getRetrofitDisplayName(measureC.retrofitName)
  const valueIncreasePct = measureD.economics.realEstateValuationIncreaseInPct
  const [isRenovationCostsPopupOpen, setIsRenovationCostsPopupOpen] = useState(false)
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false)

  const { demandBasedAssessment, consumptionBasedAssessment } = assessment

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        {/* Left - Measure Image */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'start',
            gap: 2,
          }}
        >
          <Box
            component="img"
            src={getMeasureImage(measureC.retrofitName)}
            alt={displayName}
            sx={{
              width: '100%',
              aspectRatio: '1.05',
              objectFit: 'cover',
              borderRadius: '8px',
              display: 'block',
            }}
          />

          {getRetrofitExtraInfo(measureC)}
        </Box>

        {/* Divider - only visible on non-mobile */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: '1px',
            backgroundColor: '#E1E1E4',
          }}
        />

        {/* Right - Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: { xs: 2, sm: 8 },
          }}
        >
          {/* First Column - Vorteile */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              Vorteile
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '12px',
                color: '#74777F',
              }}
            >
              {getRetrofitAdvantages(measureC.retrofitName)}
            </Typography>
          </Box>

          {/* Second Column - Bottom components */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SavingsPotential
              showBedarfVerbrauch={false}
              avgEnergyCostSavingsWithinFiveYears={{
                inCt: measureC.economics.avgEnergyCostSavingsInCtWithinFiveYears,
                inPct: measureC.economics.avgEnergyCostSavingsInPctWithinFiveYears,
              }}
              annualFinalEnergyInKwh={{
                current:
                  consumptionBasedAssessment.currentState.energetics.annualFinalEnergyInKwh.total,
                retrofit: measureC.energetics.annualFinalEnergyInKwh.total,
              }}
            />
            {valueIncreasePct > 0.01 && <PropertyValueIncrease percentage={valueIncreasePct} />}
          </Box>
        </Box>
      </Box>

      {/* Übersicht Section */}
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '14px',
          marginTop: 1,
          marginBottom: 2,
        }}
      >
        Übersicht
      </Typography>

      {/* Grid with 4 items */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)', // 2 columns on mobile
            sm: 'repeat(5, 1fr)', // 5 columns on larger screens
          },
          gap: { xs: 2, sm: 1 },
        }}
      >
        {/* Energieklassen */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              fontSize: '10px',
              color: '#464A54',
              fontWeight: 700,
            }}
          >
            Energieklassen{' '}
            <Box component="span" sx={{ color: '#74777F', fontWeight: 500 }}>
              (Bedarf)
            </Box>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {/* Current energy label box */}
            <Box
              sx={{
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                backgroundColor: getEnergyLabelColor(
                  demandBasedAssessment.currentState.energetics.energyLabel
                ),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                }}
              >
                {demandBasedAssessment.currentState.energetics.energyLabel}
              </Typography>
            </Box>

            {/* Arrow */}
            <ChevronRightIcon style={{ width: 16, height: 16, color: '#74777F' }} />

            {/* New energy label box */}
            <Box
              sx={{
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                backgroundColor: getEnergyLabelColor(measureD.energetics.energyLabel),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                }}
              >
                {measureD.energetics.energyLabel}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Amortisationszeit */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              fontSize: '10px',
              color: '#464A54',
              fontWeight: 700,
            }}
          >
            Amortisationszeit
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {formatAmortization(measureC.economics.amortizationTimeInYears ?? null)}
          </Typography>
        </Box>

        {/* Sanierungskosten */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              fontSize: '10px',
              color: '#464A54',
              fontWeight: 700,
            }}
          >
            Sanierungskosten
          </Typography>
          {measureC.economics.retrofitCostsInCt?.mean ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Typography
                sx={{
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  backgroundColor: '#F5F5F7',
                  padding: '3px 8px',
                  borderRadius: '18px',
                  fontSize: '10px',
                  fontWeight: 600,
                  color: '#464A54',
                  width: 'fit-content',
                }}
              >
                Ab {formatCosts(measureC.economics.retrofitCostsInCt.mean)}
              </Typography>
              <InfoCircleIcon
                style={{ width: 18, height: 18, color: '#464A54', cursor: 'pointer' }}
                onClick={() => setIsRenovationCostsPopupOpen(true)}
              />
            </Box>
          ) : (
            <Box>k. A.</Box>
          )}
        </Box>

        {/* max. Förderung */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              fontSize: '10px',
              color: '#464A54',
              fontWeight: 700,
            }}
          >
            max. Förderung
          </Typography>
          <Typography
            sx={{
              display: 'inline-flex',
              alignItems: 'baseline',
              backgroundColor: '#E5F5E7',
              padding: '3px 8px',
              borderRadius: '18px',
              fontSize: '10px',
              color: '#464A54',
              fontWeight: 600,
              width: 'fit-content',
            }}
          >
            {formatSubsidy(
              measureC.economics.retrofitCostsInCt?.mean ?? null,
              measureC.economics.subsidyRateInPct ?? null
            )}
          </Typography>
        </Box>

        {/* Kosten nach Förderung */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              fontSize: '10px',
              color: '#464A54',
              fontWeight: 700,
            }}
          >
            Kosten nach Förderung
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {formatCostsAfterSubsidy(
              measureC.economics.retrofitCostsInCt?.mean ?? null,
              measureC.economics.subsidyRateInPct ?? null
            )}
          </Typography>
        </Box>
      </Box>

      <RenovationCostsInfoPopup
        open={isRenovationCostsPopupOpen}
        onClose={() => setIsRenovationCostsPopupOpen(false)}
        onEdit={() => {
          setIsRenovationCostsPopupOpen(false)
          setIsEditPopupOpen(true)
        }}
      />

      <EditISFPDataPopup open={isEditPopupOpen} onClose={() => setIsEditPopupOpen(false)} />
    </>
  )
}

export default OverviewTab
